import React from "react";
import SimpleSelect from "@/main/react/component/SimpleSelect";
import { Localizer } from "@/main/react/Types";
import { cn } from "@/lib/utils";
import { Calendar, CalendarDays, CalendarRange, Clock3, Drama, Flame, HelpCircle, Infinity as InfinityIcon, Languages, MessageCircle, ArrowBigUp, Sunrise, Vote } from "lucide-react";
import { MetaHandler } from "@/main/common/MetaHandler";
import { languageDisplayName } from "@/main/util/languageDisplayName";
import { HomeFeedSelection, HomeOrderingStyle, HomeRange, homeOrderingStylesForFeedType, homeSelectionSupportsRange, selectionToFeedState } from "./HomePageShared";
import type { LocalizationKey } from "@/main/util/LocalizationUtil";

const READING_LANGUAGES_SETTINGS_PATH = "/settings/reading_languages";

/**
 * The feed options a LOGGED-OUT viewer must never be offered, by option `value`.
 *
 * <p><b>Why this is a module-level export rather than a `personal: true` field on each row</b> (FT-7,
 * 2026-09-20). The rows live in a `useMemo` inside the component, closing over `localizer` and the
 * group labels, so nothing outside this file could ever see them. `HomePage.spec.tsx` consequently
 * asserted the anonymous menu by hand-listing **two** of the five — `home` and `groups` — and leaking
 * any of the other three stayed green. The table grew twice (`following`/`series` with the audience
 * scopes, `boards` with the board lane) and the hand-list did not, which is the 2026-09-09
 * hand-listed-registry shape with a measured regression attached.
 *
 * <p>Exporting the VALUES rather than the rows is the smallest thing a spec can iterate: it needs no
 * localizer, no icons and no React, and adding a lane here is the same edit as gating it, so the two
 * cannot drift. A new personal lane that is not in this list is simply not gated — and
 * `HomePage.spec.tsx` asserts the anonymous option list is disjoint from this whole array, so the
 * spec grows with the product instead of being re-hand-listed.
 *
 * <p><b>`boards` is personal but sits in the EVERYWHERE group</b>, which reads as a contradiction and
 * is not: the group names the CORPUS, and what narrows that lane is a per-viewer filter whose endpoint
 * answers 401 without one. See the note on its row below.
 */
export const PERSONAL_FEED_OPTION_VALUES = ["home", "groups", "following", "series", "boards"] as const;

/** True when an option value is one an anonymous viewer must not be offered. */
function isPersonalFeedOption(value: string): boolean {
    return (PERSONAL_FEED_OPTION_VALUES as readonly string[]).includes(value);
}

/**
 * `HOME-FEED-28` (operator decision **D14**) — the filter that decides most of what is in the feed,
 * said out loud on the screen where its effect is felt.
 *
 * The row: which languages appear in this feed is set by a per-language switch inside the sidebar's
 * Language submenu, and the sidebar opens from the header avatar. On a corpus spanning two dozen
 * locales that setting decides more about what a member sees than any sort does, and it sits three
 * levels down behind a circle that reads as "my account". Nothing on the feed named the active
 * reading set, and nothing on it routed to the control — so a member seeing content in a language
 * they do not read, or missing content in one they do, had no way to connect that to a setting, and
 * the feed could not explain its own thinness.
 *
 * Four things are load-bearing and none of them is a preference:
 *
 * <ul>
 *   <li><b>A `<meta>`, never a fetch.</b> D14 settles this and it is what removes the performance
 *       objection that parked this row twice. Deciding whether the cue renders needs the reading set
 *       on EVERY home page load; `useSidebarReadingLanguages` fetches it only when the submenu is
 *       opened, so routing this through that hook would put a GET on the product's busiest surface
 *       for a line of chrome. ⛔ Do not "improve" this into a request.</li>
 *   <li><b>State-dependent, not permanent chrome.</b> Shown only when the reading set is NARROWER
 *       than the member's known languages, which against the reading-languages view resolves to
 *       "included is non-empty and muted is non-empty". A member who has narrowed nothing sees
 *       nothing, so this never becomes a banner everyone learns to ignore. It is also what keeps the
 *       control row inside the width budget `HOME-FEED-15` measured.</li>
 *   <li><b>Zero new catalog keys.</b> The visible text is the language NAMES, which already ship in
 *       all 25 catalogs behind `LANGUAGE_CONFIG`; `languageDisplayName` is the shared reader for
 *       them. The accessible name reuses `settings.reading-languages.title`, which is the control's
 *       own name. Minting a sentence here would have cost 25 values to say what the names already
 *       say.</li>
 *   <li><b>The absent-meta default is OFF, and it is checked rather than assumed.</b>
 *       `MetaHandler.parseBooleanPreference` defaults to `true` and would silently invert a meta the
 *       server means as `false` (rule 34). The readers this uses parse a tag LIST and return `[]` for
 *       an absent, blank or malformed meta, so an anonymous visitor, a server that has not deployed
 *       the metas, and a member who has muted nothing all get the same thing: no cue. The two deploy
 *       halves are order-independent and the failure mode is a missing cue, never a wrong one.</li>
 * </ul>
 *
 * ⚠️ **The backend half is not built yet** (wave 16 batch 12 was fenced to frontend files), so this
 * renders for nobody on today's server. The two metas it needs are written down exactly in
 * `designs/07-archive/runs/2026-08-26-ux-critique-wave-16/questions/w16-home-feed-frontend-HOME-FEED-28.md`.
 *
 * ⚠️ **Destination deviation, recorded:** D14 says "routes into the existing sidebar control". No
 * cross-island channel opens the sidebar's Language submenu from this page, and building one reaches
 * `MainHeader.tsx`/`MainSideBar.tsx`. This links to the control's own settings page instead — the
 * same destination `ReadingLanguageAddedStrip`'s Manage button already uses on this exact screen.
 */
export function ReadingLanguageCue(props: { localizer: Localizer }) {
    const { localizer } = props;

    // Read once at mount. The reading set cannot change without a navigation (the only writers are the
    // sidebar submenu and the settings page, both of which reload or re-render the shell), so a
    // subscription here would be a standing re-render liability on the persistent chrome's page for a
    // value that never moves under it (rule 31's sibling case).
    const readingTags = React.useMemo(() => MetaHandler.getReadingLanguageTags(), []);
    const mutedTags = React.useMemo(() => MetaHandler.getMutedReadingLanguageTags(), []);

    // The whole predicate, in one place: the member reads something, and they have deliberately put
    // something else outside the feed. Anything less than both is not a narrowed reading set.
    if (readingTags.length === 0 || mutedTags.length === 0) {
        return null;
    }

    const names = readingTags
        .map((tag) => languageDisplayName(localizer, tag))
        .filter((name) => name.length > 0);

    if (names.length === 0) {
        return null;
    }

    return (
        <a
            href={READING_LANGUAGES_SETTINGS_PATH}
            className="flex h-8 shrink-0 items-center gap-1.5 rounded-full border border-border px-2.5 text-xs text-muted-foreground standard-hover focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px]"
            aria-label={localizer("settings.reading-languages.title")}
            title={localizer("settings.reading-languages.title")}
            data-playwright-id="home-reading-language-cue"
        >
            <Languages className="size-3.5 shrink-0" aria-hidden="true" />
            <span className="max-w-[9rem] truncate">{names.join(", ")}</span>
        </a>
    );
}


/**
 * The five member tabs (SV-135 S5, design `2026-09-21-quiet-chrome-feed-visual-weight-design.md` §6.3,
 * round 29 D1/D2), in reading order: For you · All · Following · Groups · Series.
 *
 * <p>Each tab is a SCOPE, and a scope maps onto the one `HomeFeedSelection` axis the page already has:
 * the tab calls the same `onSelectionChange` the old scope select called, so `useHistoryBackedState`,
 * the URL, the persisted preference and the Back contract (rule 33) are untouched by construction.
 * "All" is the only tab that owns SEVERAL selections (the five site-wide sorts plus the two lanes); its
 * sort trigger picks among them.
 *
 * <p><b>Copy.</b> `home.feed-tab.*` is minted for this row, and "All" reuses `home.feed-type.all`. The
 * existing "Following"/"Groups"/"Series" keys name other senses (the follow button, the dashboard tabs,
 * the sidebar place), and re-valuing a key that is generic in NAME but shared across two SENSES renames
 * the other surfaces too. That is the trap this file used to document on the `my-groups` row.
 */
type HomeFeedTabScope = "for-you" | "all" | "following" | "groups" | "series";

const HOME_FEED_TABS: ReadonlyArray<{ scope: HomeFeedTabScope; labelKey: LocalizationKey }> = [
    { scope: "for-you", labelKey: "home.feed-tab.for-you" },
    { scope: "all", labelKey: "home.feed-type.all" },
    { scope: "following", labelKey: "home.feed-tab.following" },
    { scope: "groups", labelKey: "home.feed-tab.groups" },
    { scope: "series", labelKey: "home.feed-tab.series" },
];

/** Which tab a selection belongs to. `boards` and `unanswered` are lanes of the All corpus (A15). */
export function homeFeedTabForSelection(selection: HomeFeedSelection): HomeFeedTabScope {
    switch (selection) {
        case "home":
            return "for-you";
        case "following":
        case "groups":
        case "series":
            return selection;
        default:
            return "all";
    }
}

/**
 * The selection a tab press asks for. A scope tab is its own selection. "All" opens on the member's
 * REMEMBERED all-feed sort (the persisted `allOrderingStyle`), from any tab: operator answer B to the
 * round-29 parked question `r29w2-home-2b`, and what the old "Everywhere" menu did from Home. HomePage
 * owns that value (`rememberedAllState.orderingStyle`, which a sort picked inside a scope also writes).
 */
function selectionForTab(scope: HomeFeedTabScope, rememberedAllOrderingStyle: HomeOrderingStyle): HomeFeedSelection {
    switch (scope) {
        case "for-you":
            return "home";
        case "following":
        case "groups":
        case "series":
            return scope;
        case "all":
            return rememberedAllOrderingStyle;
    }
}

/**
 * The tab row. A `role="tablist"` of buttons with a roving tabindex and MANUAL activation: the arrow
 * keys move focus without leaving the strip, and Enter or Space (the button's own click) activates.
 * Automatic activation would fetch a feed and push a history entry per arrow press.
 *
 * <p>At 412px the five labels do not fit beside the sort trigger in the longer locales, so the strip
 * scrolls, never wraps: no scrollbar, a trailing fade while the end is clipped, and the selected tab is
 * scrolled into view against the strip's OWN `scrollLeft`. `scrollIntoView` would walk the document
 * too (the reason `SearchTabsNav` measures it the same way).
 */
function HomeFeedTabs(props: {
    localizer: Localizer;
    activeScope: HomeFeedTabScope;
    onSelectScope: (scope: HomeFeedTabScope) => void;
}) {
    const { localizer, activeScope, onSelectScope } = props;
    const stripRef = React.useRef<HTMLDivElement | null>(null);
    const tabRefs = React.useRef<Array<HTMLButtonElement | null>>([]);
    const [endClipped, setEndClipped] = React.useState(false);
    const activeIndex = HOME_FEED_TABS.findIndex((tab) => tab.scope === activeScope);

    const syncEndClipped = React.useCallback(() => {
        const strip = stripRef.current;
        if (!strip) {
            return;
        }
        const clipped = strip.scrollLeft + strip.clientWidth < strip.scrollWidth - 1;
        setEndClipped((previous) => (previous === clipped ? previous : clipped));
    }, []);

    const scrollTabIntoView = React.useCallback((index: number) => {
        const strip = stripRef.current;
        const tab = tabRefs.current[index];
        if (!strip || !tab) {
            return;
        }
        const tabStart = tab.offsetLeft;
        const tabEnd = tabStart + tab.offsetWidth;
        if (tabStart >= strip.scrollLeft && tabEnd <= strip.scrollLeft + strip.clientWidth) {
            return;
        }
        const centered = tabStart - (strip.clientWidth - tab.offsetWidth) / 2;
        strip.scrollLeft = Math.max(0, Math.min(centered, strip.scrollWidth - strip.clientWidth));
    }, []);

    React.useEffect(() => {
        scrollTabIntoView(activeIndex);
        syncEndClipped();
    }, [activeIndex, scrollTabIntoView, syncEndClipped]);

    React.useEffect(() => {
        const strip = stripRef.current;
        if (!strip || typeof ResizeObserver === "undefined") {
            return;
        }
        const observer = new ResizeObserver(() => syncEndClipped());
        observer.observe(strip);
        return () => observer.disconnect();
    }, [syncEndClipped]);

    const focusTabAt = (index: number) => {
        const count = HOME_FEED_TABS.length;
        const normalized = ((index % count) + count) % count;
        tabRefs.current[normalized]?.focus();
        scrollTabIntoView(normalized);
    };

    const handleKeyDown = (event: React.KeyboardEvent, index: number) => {
        switch (event.key) {
            case "ArrowRight":
                event.preventDefault();
                focusTabAt(index + 1);
                break;
            case "ArrowLeft":
                event.preventDefault();
                focusTabAt(index - 1);
                break;
            case "Home":
                event.preventDefault();
                focusTabAt(0);
                break;
            case "End":
                event.preventDefault();
                focusTabAt(HOME_FEED_TABS.length - 1);
                break;
        }
    };

    return (
        <div className="relative min-w-0 flex-1">
            <div
                ref={stripRef}
                role="tablist"
                aria-label={localizer("home.aria.feed-type")}
                className="no-scrollbar flex min-w-0 overflow-x-auto"
                onScroll={syncEndClipped}
                data-playwright-id="home-feed-tabs"
            >
                {HOME_FEED_TABS.map((tab, index) => {
                    const selected = tab.scope === activeScope;
                    return (
                        <button
                            key={tab.scope}
                            ref={(node) => {
                                tabRefs.current[index] = node;
                            }}
                            type="button"
                            role="tab"
                            aria-selected={selected}
                            tabIndex={selected ? 0 : -1}
                            data-playwright-id={`home-feed-tab-${tab.scope}`}
                            onClick={() => {
                                if (!selected) {
                                    onSelectScope(tab.scope);
                                }
                            }}
                            onKeyDown={(event) => handleKeyDown(event, index)}
                            className={cn(
                                "relative flex h-12 shrink-0 items-center whitespace-nowrap px-4 text-[15px] transition-colors hover:bg-muted/45",
                                "focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-inset focus-visible:ring-ring/50",
                                selected ? "font-semibold text-foreground" : "text-muted-foreground",
                            )}
                        >
                            <span className="relative flex h-full items-center">
                                {localizer(tab.labelKey)}
                                {selected ? (
                                    // The 3px underline sized to the LABEL, not to the padded tab.
                                    <span aria-hidden="true" className="absolute inset-x-0 bottom-0 h-[3px] rounded-full bg-primary" />
                                ) : null}
                            </span>
                        </button>
                    );
                })}
            </div>
            {endClipped ? (
                <div
                    aria-hidden="true"
                    className="pointer-events-none absolute inset-y-0 right-0 w-8 bg-gradient-to-l from-background to-transparent"
                    data-playwright-id="home-feed-tabs-fade"
                />
            ) : null}
        </div>
    );
}

/** The borderless ghost trigger shared by the sort and range selects (§6.3). */
const GHOST_TRIGGER_CLASS = "min-w-0 max-w-[13rem] border-transparent bg-transparent px-2 text-sm text-muted-foreground shadow-none";

export function HomeFeedControls(props: {
    localizer: Localizer;
    // Members get the tab row; an anonymous visitor gets no tab row at all (round 29 D1), only the
    // ghost sort trigger listing the non-personal options.
    showPersonalOptions?: boolean;
    selection: HomeFeedSelection;
    range: HomeRange;
    // The sort axis (audience-scopes design §4, HS-3a). Inside a scope the sort trigger writes this;
    // on All it picks among the site-wide SELECTIONS instead (the fused rows, now the All tab's sorts).
    orderingStyle: HomeOrderingStyle;
    // The member's remembered all-feed sort (the persisted `allOrderingStyle`): where the All tab opens.
    rememberedAllOrderingStyle?: HomeOrderingStyle;
    onSelectionChange: (value: HomeFeedSelection) => void;
    onRangeChange: (value: HomeRange) => void;
    onOrderingStyleChange: (value: HomeOrderingStyle) => void;
    className?: string;
}) {
    const {
        localizer,
        showPersonalOptions = true,
        selection,
        range,
        orderingStyle,
        rememberedAllOrderingStyle = "active",
        onSelectionChange,
        onRangeChange,
        onOrderingStyleChange,
        className,
    } = props;

    const activeScope = homeFeedTabForSelection(selection);

    /**
     * The All tab's sort trigger (and the anonymous visitor's only control): the five site-wide sorts,
     * then the two lanes of the same corpus, "No replies yet" and, for members, "Boards to vote on"
     * (A15). The lanes sit last because they are filters rather than orderings (`HOME-FEED-35`: any
     * position inside the run of five would split it). Labels are bare: the tab already names the scope,
     * so the old `Everywhere · ` prefix would say it twice.
     *
     * <p>`boards` is in `PERSONAL_FEED_OPTION_VALUES` (its endpoint needs a viewer and is never
     * allowlisted), so the anonymous list is exactly the six non-personal options they had before.
     *
     * <p>Composed STRINGS inside a memo: `SimpleSelect` compares labels by identity and rebuilds its
     * option list when one changes (rule 31's sibling case).
     */
    const allSortOptions = React.useMemo(() => ([
        { key: "new", value: "new", label: localizer("home.ordering-style.new"), icon: Sunrise, dataPlaywrightId: "home-sort-option-new" },
        { key: "active", value: "active", label: localizer("home.ordering-style.active"), icon: Flame, dataPlaywrightId: "home-sort-option-active" },
        { key: "agreed", value: "agreed", label: localizer("home.ordering-style.upvoted"), icon: ArrowBigUp, dataPlaywrightId: "home-sort-option-agreed" },
        { key: "arguments", value: "arguments", label: localizer("home.ordering-style.thoughts"), icon: MessageCircle, dataPlaywrightId: "home-sort-option-arguments" },
        { key: "controversial", value: "controversial", label: localizer("home.ordering-style.controversial"), icon: Drama, dataPlaywrightId: "home-sort-option-controversial" },
        // "No replies yet" REUSES `opening.no_replies` (a statement about the posts, rule 12); the
        // `unanswered` value, `?feed=` parameter and hook are contracts outside this file.
        { key: "unanswered", value: "unanswered", label: localizer("opening.no_replies"), icon: HelpCircle, dataPlaywrightId: "home-sort-option-unanswered" },
        // "Boards to vote on", named under rule 12 (never a debt the member owes).
        { key: "boards", value: "boards", label: localizer("discussion.board.lane.waiting"), icon: Vote, dataPlaywrightId: "home-sort-option-boards" },
    ].filter((option) => showPersonalOptions || !isPersonalFeedOption(option.value))), [localizer, showPersonalOptions]);

    // A scope's sort: the three it serves (`homeOrderingStylesForFeedType`), filtered by feed, never by
    // rank, so a shorter list keeps the same option identities.
    const scopedSortOptions = React.useMemo(() => {
        const offered = homeOrderingStylesForFeedType(selectionToFeedState(selection, orderingStyle).feedType);
        return allSortOptions.filter((option) => offered.includes(option.value as HomeOrderingStyle));
    }, [allSortOptions, orderingStyle, selection]);

    // `HOME-FEED-14`: the range family is the time family (clock, then calendar pages of growing span,
    // then the no-bound mark). `day` keeps `Clock3`; `new` wears `Sunrise` so the two never collide.
    const rangeOptions = React.useMemo(() => ([
        { key: "all", value: "all", label: localizer("home.range.all-time"), icon: InfinityIcon, dataPlaywrightId: "home-range-option-all" },
        { key: "day", value: "day", label: localizer("home.range.day"), icon: Clock3, dataPlaywrightId: "home-range-option-day" },
        { key: "week", value: "week", label: localizer("home.range.week"), icon: Calendar, dataPlaywrightId: "home-range-option-week" },
        { key: "month", value: "month", label: localizer("home.range.month"), icon: CalendarDays, dataPlaywrightId: "home-range-option-month" },
        { key: "year", value: "year", label: localizer("home.range.year"), icon: CalendarRange, dataPlaywrightId: "home-range-option-year" },
    ]), [localizer]);

    const handleSelectScope = React.useCallback((scope: HomeFeedTabScope) => {
        onSelectionChange(selectionForTab(scope, rememberedAllOrderingStyle));
    }, [onSelectionChange, rememberedAllOrderingStyle]);

    // For you has no sort (its ordering is fixed). The captions ("Showing", "Order by") are dropped;
    // each trigger keeps its `aria-label`, which `profile.ordering.label` / `home.aria.range` supply.
    let sortControl: React.ReactNode = null;
    if (activeScope === "all") {
        sortControl = (
            <div className="flex shrink-0 items-center" data-playwright-id="home-sort-select">
                <SimpleSelect
                    value={selection}
                    onValueChange={(value) => onSelectionChange(value as HomeFeedSelection)}
                    options={allSortOptions}
                    size="sm"
                    dataPlaywrightId="home-sort-trigger"
                    className={GHOST_TRIGGER_CLASS}
                    ariaLabel={localizer("profile.ordering.label")}
                />
            </div>
        );
    } else if (activeScope !== "for-you" && scopedSortOptions.length > 0) {
        sortControl = (
            <div className="flex shrink-0 items-center" data-playwright-id="home-sort-select">
                <SimpleSelect
                    value={orderingStyle}
                    onValueChange={(value) => onOrderingStyleChange(value as HomeOrderingStyle)}
                    options={scopedSortOptions}
                    size="sm"
                    dataPlaywrightId="home-sort-trigger"
                    className={GHOST_TRIGGER_CLASS}
                    ariaLabel={localizer("profile.ordering.label")}
                />
            </div>
        );
    }

    const trailing = (
        <div className="flex shrink-0 items-center gap-0.5" data-playwright-id="home-feed-controls-trailing">
            {sortControl}
            {homeSelectionSupportsRange(selection, orderingStyle) ? (
                <div className="flex shrink-0 items-center" data-playwright-id="home-range-select">
                    <SimpleSelect
                        value={range}
                        onValueChange={(value) => onRangeChange(value as HomeRange)}
                        options={rangeOptions}
                        size="sm"
                        dataPlaywrightId="home-range-trigger"
                        className={GHOST_TRIGGER_CLASS}
                        ariaLabel={localizer("home.aria.range")}
                    />
                </div>
            ) : null}
            {/* `HOME-FEED-28`. LAST: it renders only in the narrowed-reading-set state, and last is the
                position that can never displace a feed control. */}
            <ReadingLanguageCue localizer={localizer} />
        </div>
    );

    if (!showPersonalOptions) {
        // Round 29 D1: no tab row for an anonymous visitor. Every tab is a personal lane, so the row
        // would hold a single tab: chrome with no choice. Start-aligned below `sm` (`HOME-FEED-15`: the
        // row must never push its first control off a phone), right-aligned from `sm` up.
        return (
            <div className={cn("flex w-full min-w-0 items-center justify-start sm:justify-end", className)} data-playwright-id="home-feed-controls">
                {trailing}
            </div>
        );
    }

    return (
        <div
            className={cn("flex w-full min-w-0 items-center border-b border-divider", className)}
            data-playwright-id="home-feed-controls"
        >
            <HomeFeedTabs localizer={localizer} activeScope={activeScope} onSelectScope={handleSelectScope} />
            {trailing}
        </div>
    );
}
